// frontend/src/pages/Quizzes.tsx

import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { quizService, QuizApiError } from '../services/quiz.service';
import { useAuthStore } from '../store/authStore';
import { ActiveQuiz, QuizDifficulty, QuizResult, QuizStats } from '../types/quiz.types';
import { QuizSetupCard } from '../components/quizzes/QuizSetupCard';
import { QuizQuestionCard } from '../components/quizzes/QuizQuestionCard';
import { QuizResultView } from '../components/quizzes/QuizResultView';
import { DifficultySelector } from '../components/quizzes/DifficultySelector';
import { QuizStatsCard } from '../components/quizzes/QuizStatsCard';
import '../styles/quizzes.css';

type QuizPhase = 'setup' | 'in_progress' | 'result';

export function QuizzesPage() {
  const fetchProfile = useAuthStore((s) => s.fetchProfile);
  const [searchParams] = useSearchParams();
  const topicParam = searchParams.get('topic');
  const autoStartParam = searchParams.get('autoStart');
  const autoStartedRef = useRef(false);

  const [phase, setPhase] = useState<QuizPhase>('setup');
  const [difficulty, setDifficulty] = useState<QuizDifficulty>('medium');
  const [quiz, setQuiz] = useState<ActiveQuiz | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<QuizResult | null>(null);

  const [isGenerating, setIsGenerating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [stats, setStats] = useState<QuizStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    quizService
      .getStats()
      .then(setStats)
      .catch(() => {
        /* istatistik alınamazsa sessizce yoksay, kritik değil */
      })
      .finally(() => setStatsLoading(false));
  }, [phase]); // her quiz tamamlandığında istatistikleri tazele

  useEffect(() => {
    if (topicParam && autoStartParam === 'true' && !autoStartedRef.current) {
      autoStartedRef.current = true;
      handleStartQuiz(topicParam);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicParam, autoStartParam]);

  async function handleStartQuiz(topic: string) {
    setIsGenerating(true);
    setError(null);
    try {
      const activeQuiz = await quizService.generateQuiz({ topic, difficulty, questionCount: 5 });
      setQuiz(activeQuiz);
      setAnswers({});
      setCurrentIndex(0);
      setResult(null);
      setPhase('in_progress');
    } catch (err) {
      setError(err instanceof QuizApiError ? err.message : 'Quiz oluşturulamadı.');
    } finally {
      setIsGenerating(false);
    }
  }

  function handleSelectAnswer(answer: string) {
    if (!quiz) return;
    const questionId = quiz.questions[currentIndex].id;
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
  }

  function handleNext() {
    if (!quiz) return;
    if (currentIndex < quiz.questions.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else {
      handleSubmit();
    }
  }

  function handlePrevious() {
    setCurrentIndex((i) => Math.max(0, i - 1));
  }

  async function handleSubmit() {
    if (!quiz) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await quizService.submitQuiz(quiz.quizId, answers);
      setResult(res);
      setPhase('result');
      await fetchProfile(); // navbar'daki XP'yi güncelle
    } catch (err) {
      setError(err instanceof QuizApiError ? err.message : 'Quiz gönderilemedi.');
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleRetry() {
    setAnswers({});
    setCurrentIndex(0);
    setResult(null);
    setPhase('in_progress');
  }

  function handleNewQuiz() {
    setQuiz(null);
    setResult(null);
    setPhase('setup');
  }

  return (
    <div className="quizzes-page">
      <header className="quizzes-header">
        <h1>Quizler 📝</h1>
        <p>Kodlama, Matematik, Fen, Türkçe, İngilizce ve Mantık derslerinde AI ile kişiselleştirilmiş quizler</p>
      </header>

      <div className="grid grid-2 quizzes-grid">
        <div>
          {error && <div className="quiz-error-banner">{error}</div>}

          {phase === 'setup' && <QuizSetupCard onStart={handleStartQuiz} isLoading={isGenerating} />}

          {phase === 'in_progress' && quiz && (
            <>
              <QuizQuestionCard
                question={quiz.questions[currentIndex]}
                questionIndex={currentIndex}
                totalQuestions={quiz.questions.length}
                selectedAnswer={answers[quiz.questions[currentIndex].id]}
                onSelect={handleSelectAnswer}
                topicLabel={quiz.title}
              />
              <div className="quiz-nav-buttons">
                <button
                  type="button"
                  className="lesson-btn secondary"
                  onClick={handlePrevious}
                  disabled={currentIndex === 0}
                >
                  ⬅️ Önceki
                </button>
                <button
                  type="button"
                  className="lesson-btn primary"
                  onClick={handleNext}
                  disabled={isSubmitting || !answers[quiz.questions[currentIndex].id]}
                >
                  {isSubmitting
                    ? '⏳ Gönderiliyor...'
                    : currentIndex === quiz.questions.length - 1
                      ? '✅ Bitir'
                      : '➡️ Sonraki'}
                </button>
              </div>
            </>
          )}

          {phase === 'result' && quiz && result && (
            <QuizResultView quiz={quiz} result={result} onRetry={handleRetry} onNewQuiz={handleNewQuiz} />
          )}
        </div>

        <div>
          <QuizStatsCard stats={stats} isLoading={statsLoading} />
          {phase === 'setup' && (
            <div style={{ marginTop: 24 }}>
              <DifficultySelector value={difficulty} onChange={setDifficulty} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
